import { z } from 'zod';
/**
 * Roles de las apps administrados desde la suite (GUIA-AGENTES.md §3.9). Cada app publica su catálogo por entidad
 * (incluye los roles propios que la entidad haya creado) y la suite asigna, por persona y app, una lista de roles. La
 * app sigue siendo dueña de qué permite cada rol y de sus reglas: valida lo que llega y rechaza lo que no se puede
 * asignar desde afuera (p. ej. SUPER en SGDEA).
 */
export const ROLE_CODE = /^[A-Z0-9_]{1,60}$/;
export const roleCodeSchema = z.string().regex(ROLE_CODE, 'Código de rol inválido (UPPER_SNAKE)');
export const appRoleSchema = z.object({
    code: roleCodeSchema,
    name: z.string().min(1).max(120),
    description: z.string().max(400).nullable().optional(),
    /** El rol se asigna limitado a un alcance de la app (p. ej. un área en SGDEA): exige `scope` al asignarlo. */
    scoped: z.boolean().default(false),
    /** Se puede tener sin alcance además de con alcance (SGDEA: un rol para toda la entidad o para un área). */
    scopeOptional: z.boolean().default(true),
    /** Rol que recibe quien gana acceso a la app sin que nadie elija rol. */
    default: z.boolean().default(false),
    /** Rol que recibe un administrador de la entidad en la suite. */
    admin: z.boolean().default(false),
    /** `false`: la app lo muestra pero no acepta que la suite lo asigne. */
    assignable: z.boolean().default(true),
    /** Lo creó la entidad (no viene con la app). */
    custom: z.boolean().default(false),
});
/** Alcances posibles de los roles `scoped`: un nombre («Área») y sus opciones (las áreas de la entidad). */
export const roleScopesSchema = z.object({
    label: z.string().min(1).max(60),
    options: z.array(z.object({ value: z.string().min(1).max(100), label: z.string().min(1).max(200), parent: z.string().nullable().optional() })),
});
/** Catálogo de roles de una app para una entidad: `GET <provisioning_path>/:slug/roles` y `PUT /service/entities/:slug/roles`. */
export const appRoleCatalogSchema = z.object({
    roles: z.array(appRoleSchema),
    scopes: roleScopesSchema.nullable().default(null),
});
/** Un rol asignado: el código y, si el rol es `scoped`, su alcance. */
export const roleGrantSchema = z.object({ role: roleCodeSchema, scope: z.string().min(1).max(100).nullable().optional() });
/** Clave estable de una asignación (para comparar y deduplicar). */
export const grantKey = (g) => `${g.role}@${g.scope ?? ''}`;
/**
 * Valida una lista de roles contra el catálogo: que existan y se puedan asignar, que los `scoped` traigan un alcance
 * del catálogo (o ninguno si el alcance es opcional) y que no haya repetidos. Devuelve los problemas en español.
 */
export function validateGrants(grants, catalog) {
    const problems = [];
    const seen = new Set();
    const scopes = new Set(catalog.scopes?.options.map((o) => o.value) ?? []);
    for (const g of grants) {
        const role = catalog.roles.find((r) => r.code === g.role);
        if (!role) {
            problems.push(`El rol ${g.role} no existe`);
            continue;
        }
        if (!role.assignable)
            problems.push(`El rol ${role.name} no se asigna desde la suite`);
        if (g.scope) {
            if (!role.scoped)
                problems.push(`El rol ${role.name} no se limita a un alcance`);
            else if (!scopes.has(g.scope))
                problems.push(`El alcance ${g.scope} no existe para ${role.name}`);
        }
        else if (role.scoped && !role.scopeOptional)
            problems.push(`El rol ${role.name} necesita ${catalog.scopes?.label?.toLowerCase() ?? 'un alcance'}`);
        const k = grantKey(g);
        if (seen.has(k))
            problems.push(`El rol ${role.name} está repetido`);
        seen.add(k);
    }
    return problems;
}
/** Roles por defecto al dar acceso a la app sin elegirlos: el `admin` para administradores, si no el `default`. */
export function defaultGrants(catalog, entityRole) {
    const pick = (entityRole === 'ADMIN' ? catalog.roles.find((r) => r.admin) : undefined) ?? catalog.roles.find((r) => r.default);
    return pick ? [{ role: pick.code, scope: null }] : [];
}
//# sourceMappingURL=roles.js.map